// What changed in one vehicle-status update, as the board's expanded history shows it.
//
// Lifted out of VsmStatusHistoryList so the VSM History report renders the same
// "Wash CLEANED -> DIRTY" list from the same rules. Both views diff a record against the
// next-older record for that vehicle; they differ only in where that neighbour comes from
// (the board loads one vehicle's history, the report holds a window of every vehicle's).
import moment from 'moment'
import { statusLabel } from '@/utils/vehicle_status_labels'
import { VSM_HISTORY_FIELDS, VSM_FIELDS } from '@/utils/vsm_fields'

// the fields worth diffing, in display order. keys match the vehicle-info history records
// (telematics-fed tenants carry obdOdometer/fuel_percent/def_percent; SMS-fed ones carry
// odometer/fuel_level/def_level).
export const TRACKED_FIELDS = [
  ['vehicle_status', 'Wash'],
  ['maintenance_status', 'Maint'],
  ['notes', 'Notes'],
  ['wheelchair', 'WC'],
  ['stayanddo', 'S&D'],
  ['tote', 'Tote'],
  ['vehicle_base', 'Base'],
  ['company', 'Company'],
  ['obdOdometer', 'Odometer'],
  ['odometer', 'Odometer'],
  ['fuel_percent', 'Fuel %'],
  ['fuel_level', 'Fuel'],
  ['def_percent', 'DEF %'],
  ['def_level', 'DEF'],
]

// Bookkeeping that is on every record and is not a change anyone means: the row's own
// identity and stamps, the TTL, and the annotations this module hangs off a row.
const NOT_A_CHANGE = new Set([
  'tenant_id', 'vehicle_name', 'processed_time', 'expiry_time', 'updated_by',
  'changes', 'no_earlier', 'status_changed', 'maint_changed',
  'source_label', 'action_label',
])

const ACRONYMS = new Set(['RTA', 'DVIR', 'DEF', 'WC', 'GPS', 'TBN', 'ETA', 'PM', 'OOS', 'ID'])

const KNOWN_LABELS = (() => {
  const labels = {}
  // the catalogs the boards and the history table already label these with, history first
  for (const list of [VSM_FIELDS, VSM_HISTORY_FIELDS])
    for (const field of list)
      if (field.label) labels[field.key] = field.label
  for (const [key, label] of TRACKED_FIELDS) labels[key] = label
  return labels
})()

export function labelForKey(key) {
  if (KNOWN_LABELS[key]) return KNOWN_LABELS[key]
  return String(key).split(/[_\s]+/)
    .map(w => (ACRONYMS.has(w.toUpperCase())
      ? w.toUpperCase()
      : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')
}

// TRACKED_FIELDS lead so the fields people scan for stay in the same order, then whatever
// else the two records carry - a cleaned time, a cleaned number, an RTA status. Diffing the
// record rather than a whitelist is the point: a field nobody listed still changed.
export function fieldsForRecords(entry, older) {
  const fields = TRACKED_FIELDS.slice()
  const listed = new Set(TRACKED_FIELDS.map(([key]) => key))
  const extras = new Set()
  for (const record of [entry, older])
    for (const key of Object.keys(record || {}))
      if (!listed.has(key) && !NOT_A_CHANGE.has(key)) extras.add(key)
  // by label, not by key: last_cleaned_number sorts above last_cleaned_time, which reads
  // as "Cleaned #" above "Cleaned"
  const extraFields = Array.from(extras).map(key => [key, labelForKey(key)])
  extraFields.sort((a, b) => a[1].localeCompare(b[1]) || a[0].localeCompare(b[0]))
  return fields.concat(extraFields)
}

// 2026-09-18T13:40:31.630682Z reads as noise in a cell that is meant to be scanned; the
// table's own time columns use MM/DD hh:mm A (formatDateTimeSmall), so these match them.
// moment renders in whatever default zone the page set from the tenant record.
const ISO_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function normalizeValue(key, value) {
  if (value === true) return 'Yes'
  if (value === false) return null
  if (value == null || value === '') return null
  if (key === 'vehicle_status' || key === 'maintenance_status')
    return statusLabel(value)
  const text = String(value)
  if (ISO_STAMP.test(text)) {
    const m = moment(text)
    if (m.isValid()) return m.format('MM/DD hh:mm A')
  }
  if (ISO_DATE.test(text)) {
    const m = moment(text, 'YYYY-MM-DD')
    if (m.isValid()) return m.format('MM/DD/YY')
  }
  return text
}

// One entry per field whose value moved between `older` and `entry`.
// `older` null means nothing to compare against, which is not the same as nothing changing.
export function entryChanges(entry, older, fields = TRACKED_FIELDS) {
  const changes = []
  const seenLabels = new Set()
  if (!entry) return changes
  for (const [key, label] of fields) {
    // obdOdometer/odometer (etc.) alias the same reading; first present wins
    if (seenLabels.has(label))
      continue
    if (entry[key] == null && (!older || older[key] == null))
      continue
    seenLabels.add(label)
    const cur = normalizeValue(key, entry[key])
    const prev = older ? normalizeValue(key, older[key]) : null
    if (cur === prev)
      continue
    changes.push({ key, label, from: prev == null ? '—' : prev, to: cur == null ? '—' : cur })
  }
  return changes
}

// Walk a window of history rows for many vehicles, newest first, and hang each row's
// changes off it.
//
// A vehicle's oldest row in the window has no neighbour to diff against, so it carries
// no_earlier and no changes at all. entryChanges on its own would list every field as
// "— -> value", which is right for the board (that list is the vehicle's own history, so
// the oldest entry is where the values start) and wrong here: a date range cuts every
// vehicle mid-life, and a screen full of fabricated first changes is exactly the noise a
// status chip is meant to cut through.
// allFields diffs everything the two records carry rather than the curated list - what the
// report's Changes column wants, since a cleaned time or a cleaned number is exactly the
// change someone is looking for there.
export function annotateChanges(rows, { allFields = false } = {}) {
  const previousByVehicle = {}
  const annotated = []
  // newest first on the way in, so walk it backwards to meet each vehicle's oldest row first
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i]
    const vehicle = row.vehicle_name
    const older = Object.prototype.hasOwnProperty.call(previousByVehicle, vehicle)
      ? previousByVehicle[vehicle]
      : null
    const fields = allFields ? fieldsForRecords(row, older) : TRACKED_FIELDS
    const changes = older ? entryChanges(row, older, fields) : []
    annotated[i] = Object.assign({}, row, {
      changes,
      no_earlier: !older,
      status_changed: changes.some(c => c.key === 'vehicle_status'),
      maint_changed: changes.some(c => c.key === 'maintenance_status'),
    })
    previousByVehicle[vehicle] = row
  }
  return annotated
}
